import { Location } from '@angular/common';
import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, mapTo, switchMap, tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { defaultQueueId } from '../../coaching-observation/observation-interaction/interaction.observation.form';
import { adjustDropdownWidth } from '../../coaching-observation/observation-interaction/interaction.observation.function';
import { QueueItem } from '../../coaching-tags/models/queue.coaching.model';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { NAV_HEIGHT } from '../../nav/nav.model';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { Form, QuestionType, SwitchboardService } from '../core';
import { useSelector } from '../store-use-selector';

import { TranslationKeyMap } from './form.i18n';
import { formReducer } from './form.reducer';
import { FormState } from './form.state';
import { checkScore } from './function/check-score.function';
import { rollupScore } from './function/rollup-score.function';
import { EvalFormValue } from './model/eval-form-value.model';
import { GroupFormValue } from './model/group-form-value.model';
import {
    hidePublishConfirmDialog,
    showPublishConfirmDialog,
} from './state/dialog.action';
import { reload } from './state/reload.action';
import { reset } from './state/reset.action';
import { save } from './state/save.action';

@Component({
    selector: 'cmps-eval-form',
    templateUrl: './form.component.html',
    host: {
        class: 'cmps-eval-form',
    },
    providers: [DateProviders],
})
@TakeUntilDestroy()
@UseStore<FormState>('evaluation.form', formReducer)
export class FormComponent
    implements OnInit, OnDestroy, StateToProps<FormState>
{
    state: FormState;

    @ViewChild('dropDownQueue', { read: ElementRef }) _dropdownRef: ElementRef;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _QuestionType = QuestionType;

    /** @private */
    _evalFormGroup: FormGroup;

    /** @private */
    _viewOnly: boolean;

    /** @private */ _queueOpts: Array<{ value: number; label: string }>;

    private _formErrors: {
        evalFail: boolean;
        groupFails: AbstractControl[];
        sectionFails: AbstractControl[];
        questionFails: AbstractControl[];
    };

    private readonly _autoSaveSubj: Subject<void>;
    private _autoSaveSub: Subscription;
    private _blockAutoSave: boolean; // TODO move to state?

    private _valChangeSub: Subscription;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _router: Router,
        private readonly _urlSerializer: UrlSerializer,
        private readonly _location: Location,
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _snackBar: CmpsSnackBar,
        private readonly _dialog: CmpsDialog,
        private readonly _renderer: Renderer2,
        @Optional() private readonly _switchboardService: SwitchboardService,
    ) {
        this._autoSaveSubj = new Subject();
    }

    ngOnInit(): void {
        // TODO move to separate file

        this._loadQueues();
        useSelector<Form>('evaluation.form.form', () => null)
            .pipe(
                // skip(1), // ignore initial value when connecting to store
                tap((state) => {
                    const root = this._formBuilder.group({
                        // read-only
                        draftId: [this.state?.draft?.draftId],
                        responseId: [this.state?.response?.responseId],
                        templateVersionId: [state?.templateVersionId],
                        name: [state?.name],
                        // userId: [state?.userId ?? this._authService.user?.id],
                        userId: [this._authService.user?.id], // this should be ok for both draft and edit-publish
                        targetUserId: [
                            state?.targetUserId ?? this.state?.targetUser?.id,
                        ],
                        targetUserName: [state?.targetUserName],
                        maxScore: [state?.maxScore],

                        // dynamic
                        eventDate: [state?.eventDate ?? new Date()],
                        eventTime: [state?.eventDate ?? new Date()],
                        contactLength: [state?.contactLength ?? 0],
                        queue: [state?.queue ?? defaultQueueId],
                        evaluationDate: [state?.evaluationDate ?? new Date()],
                        score: [0],
                        diffMaxScore: [state?.maxScore],
                        percentage: [0],
                    });

                    const groups = this._formBuilder.array([]);
                    state?.groups?.forEach((g, gi) => {
                        const grp = this._formBuilder.group({
                            // read-only
                            groupId: [g.groupId],
                            name: [g.name],
                            maxScore: [g.maxScore],

                            // dynamic
                            score: [0],
                            diffMaxScore: [g.maxScore],
                            percentage: [0],
                            hasChildError: [false],
                            collapsed: [
                                this.state?.response?.responseId
                                    ? true
                                    : gi !== 0,
                            ],
                        });
                        const sections = this._formBuilder.array([]);
                        g?.sections?.forEach((s) => {
                            const sec = this._formBuilder.group({
                                // read-only
                                sectionId: [s.sectionId],
                                name: [s.name],
                                maxScore: [s.maxScore],

                                // dynamic
                                score: [0],
                                diffMaxScore: [s.maxScore],
                                percentage: [0],
                                hasChildError: [false],
                            });
                            const questions = this._formBuilder.array([]);
                            s?.questions?.forEach((q) => {
                                const ques = this._formBuilder.group({
                                    // read-only
                                    questionId: [q.questionId],
                                    text: [q.text],
                                    hasChildError: [false],
                                    type: [q.type],
                                    options: [q.options],
                                    scoreConstraints: [q.scoreConstraints],
                                    visibility: [
                                        state?.permissionContext?.questions?.find(
                                            (e) =>
                                                e.questionId === q.questionId,
                                        ),
                                    ],

                                    // dynamic
                                    optionId: [
                                        q.response?.optionValue,
                                    ] /* for radioButton */,
                                    optionReasonValue: [
                                        q.response?.optionReasonValue,
                                    ] /* for radioButton */,
                                    boolValue: [
                                        q.response?.boolValue,
                                    ] /* for checkBox */,
                                    textValue: [
                                        q.response?.textValue,
                                    ] /* for textBox */,
                                    score: [],
                                });
                                questions.push(ques);
                            });
                            sec.addControl('questions', questions);
                            sections.push(sec);
                        });
                        grp.addControl('sections', sections);
                        groups.push(grp);
                    });
                    root.addControl('groups', groups);

                    const comments = this._formBuilder.array([
                        state?.comments?.[0]?.text ?? null,
                    ]);
                    root.addControl('comments', comments);

                    this._evalFormGroup = root;

                    this._formErrors = {
                        evalFail: false,
                        groupFails: [],
                        sectionFails: [],
                        questionFails: [],
                    };

                    this._evalFormGroup.addValidators(
                        (_: AbstractControl): ValidationErrors | null => {
                            return {
                                eval: this._formErrors.evalFail,
                                groups: this._formErrors.groupFails.reduce(
                                    (obj, e) => {
                                        obj[e.value.groupId] = true;
                                        return obj;
                                    },
                                    {},
                                ),
                                sections: this._formErrors.sectionFails.reduce(
                                    (obj, e) => {
                                        obj[e.value.sectionId] = true;
                                        return obj;
                                    },
                                    {},
                                ),
                                questions:
                                    this._formErrors.questionFails.reduce(
                                        (obj, e) => {
                                            obj[e.value.questionId] = true;
                                            return obj;
                                        },
                                        {},
                                    ),
                            };
                        },
                    );

                    this._evalFormGroup.addValidators(
                        (_: AbstractControl): ValidationErrors | null => {
                            const form = this._evalFormGroup;
                            const value: EvalFormValue = _.value;
                            const questionReasons = {};

                            (
                                form?.controls?.groups as FormArray
                            ).controls.forEach((g, gi) => {
                                (
                                    (g as FormGroup)?.controls
                                        ?.sections as FormArray
                                ).controls.forEach((s, si) => {
                                    (
                                        (s as FormGroup)?.controls
                                            ?.questions as FormArray
                                    ).controls.forEach((q, qi) => {
                                        const qv =
                                            value.groups[gi].sections[si]
                                                .questions[qi];

                                        if (
                                            (q as FormGroup).controls
                                                ?.optionReasonValue?.errors !=
                                            null
                                        ) {
                                            questionReasons[qv.questionId] =
                                                true;
                                        }
                                    });
                                });
                            });

                            return {
                                questionReasons:
                                    Object.keys(questionReasons).length === 0
                                        ? null
                                        : questionReasons,
                            };
                        },
                    );

                    this._autoSaveSub?.unsubscribe();
                    this._blockAutoSave = false;
                    if (this.state?.draft != null && this._viewOnly !== true) {
                        this._autoSaveSub = this._autoSaveSubj
                            .pipe(
                                debounceTime(5000),
                                filter(() => !this._blockAutoSave),
                                tap(() => {
                                    this._onSaveClick();
                                }),
                                takeUntilDestroy(this),
                            )
                            .subscribe();
                    }

                    this._valChangeSub?.unsubscribe();
                    this._valChangeSub = this._evalFormGroup.valueChanges
                        .pipe(
                            tap((value: EvalFormValue) => {
                                /* TODO revise. skip textBox value changes??? */

                                this._sanitizeContactLength();
                                this._applyScoring(value);

                                if (this.state?.response?.responseId == null) {
                                    this._autoSaveSubj.next();
                                }
                            }),
                            takeUntilDestroy(this),
                        )
                        .subscribe();

                    if (
                        (this.state?.draft?.draftId != null ||
                            (this.state?.response?.responseId != null &&
                                this.state?.canEditResponse === true)) &&
                        this._viewOnly !== true
                    ) {
                        this._applyScoring(this._evalFormGroup.value);
                    }

                    if (
                        this.state?.draft?.draftId != null &&
                        this._viewOnly !== true
                    ) {
                        const dest = this._router.createUrlTree(
                            ['drafts', this.state.draft.draftId.toLowerCase()],
                            {
                                relativeTo: this._activatedRoute.parent,
                                queryParams: {
                                    userId: state?.targetUserId,
                                    templateVersionId: state?.templateVersionId,
                                },
                                queryParamsHandling: 'merge',
                            },
                        );
                        const matches = this._router.isActive(dest, {
                            matrixParams: 'exact',
                            queryParams: 'subset',
                            paths: 'exact',
                            fragment: 'ignored',
                        });
                        if (!matches) {
                            this._location.replaceState(
                                this._urlSerializer.serialize(dest),
                            );
                        }
                    }

                    if (
                        (this.state?.response?.responseId != null &&
                            this.state?.canEditResponse !== true) ||
                        this._viewOnly === true
                    ) {
                        this._evalFormGroup.disable();
                    } else if (
                        this.state?.response?.responseId != null &&
                        this.state?.canEditResponse === true
                    ) {
                        // eslint-disable-next-line @typescript-eslint/dot-notation
                        this._evalFormGroup.controls[
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            'evaluationDate'
                        ].disable();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // TODO move to separate file
        useSelector<boolean>('evaluation.form.pending.reload', () => null)
            .pipe(
                switchMap((state) => {
                    // TODO revise. is 1 second enough time. how should feedback be given to the user until this happens
                    return timer(1000).pipe(
                        tap(() => {
                            const above =
                                this._el.nativeElement?.previousElementSibling?.getBoundingClientRect();
                            if (above == null) {
                                return;
                            }
                            window.scrollTo(above.x, above.bottom - NAV_HEIGHT);
                        }),
                        mapTo(state),
                    );
                }),
                tap((state) => {
                    const showSnack =
                        state === false &&
                        this.state?.draft?.draftId != null &&
                        this._activatedRoute.snapshot.parent?.parent?.routeConfig?.path?.toLowerCase() ===
                            'evaluation';
                    if (!showSnack) {
                        return;
                    }

                    this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                        duration: 5000,
                        placement: 'bottom-right',
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        data: {
                            text: this._translateService.instant(
                                TranslationKeyMap._10008_EXISTING_DRAFT_LOADED,
                            ),
                        } as CmpsTextOnlySnackBarOptions,
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // TODO move to separate file
        let wasSavingDraft = false;
        useSelector<boolean>('evaluation.form.pending.save', () => null)
            .pipe(
                filter((state) => {
                    const result =
                        wasSavingDraft === true &&
                        state === false &&
                        this.state?.draft?.draftId != null;
                    wasSavingDraft =
                        state === true && this.state.pending.publish !== true;
                    return result;
                }),
                tap(() => {
                    this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                        duration: 5000,
                        placement: 'bottom-right',
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        data: {
                            text: this._translateService.instant(
                                TranslationKeyMap._10009_DRAFT_SAVED,
                            ),
                        } as CmpsTextOnlySnackBarOptions,
                    });
                    this._switchboardService?.sbLandingDidUpdate(
                        this.state?.draft?.draftId,
                        this.state?.response?.responseId,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // TODO move to separate file
        let wasPublishing = false;
        useSelector<boolean>('evaluation.form.pending.publish', () => null)
            .pipe(
                filter((state) => {
                    const result =
                        wasPublishing === true &&
                        state === false; /* && TODO check for responseId */
                    wasPublishing = state;
                    return result;
                }),
                tap(() => {
                    this._blockAutoSave = false;
                    this._switchboardService?.sbLandingDidUpdate(
                        this.state?.draft?.draftId,
                        this.state?.response?.responseId,
                    );
                    // TODO revise
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._router.navigate([
                        this._activatedRoute.parent?.parent?.routeConfig?.path,
                    ]);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        combineLatest([
            this._activatedRoute.paramMap,
            this._activatedRoute.queryParamMap,
        ])
            .pipe(
                tap(() => {
                    const params = this._activatedRoute.snapshot.paramMap;
                    const queryParams =
                        this._activatedRoute.snapshot.queryParamMap;

                    const draftId = params.get('draftId')?.trim();

                    let responseId = parseInt(params.get('responseId'));
                    responseId = !isNaN(responseId) ? responseId : null;

                    /** userId from query param is targetUserId */
                    let userId = parseInt(queryParams.get('userId'));
                    userId = !isNaN(userId) ? userId : null;

                    let templateVersionId = parseInt(
                        queryParams.get('templateVersionId'),
                    );
                    templateVersionId = !isNaN(templateVersionId)
                        ? templateVersionId
                        : null;

                    // TODO revise. move to state?
                    this._viewOnly =
                        this._activatedRoute.snapshot.queryParamMap
                            .get('viewOnly')
                            ?.toLowerCase() === 'true';

                    /** guard for evaluation/drafts?userId=<number>&templateVersionId=<number> routing to
                     * evaluation/drafts/:draftId?userId=<number>&templateVersionId=<number> */
                    const sameDraftId =
                        draftId != null &&
                        draftId.toLowerCase() ===
                            this.state?.draft?.draftId?.toLowerCase();
                    const sameResponseId =
                        responseId != null &&
                        responseId === this.state?.response?.responseId;
                    const sameUserId =
                        userId != null &&
                        userId === this.state?.form?.targetUserId;
                    const sameTemplateVersionId =
                        templateVersionId != null &&
                        templateVersionId ===
                            this.state?.form?.templateVersionId;
                    if (
                        sameUserId &&
                        sameTemplateVersionId &&
                        (sameDraftId || sameResponseId)
                    ) {
                        return;
                    }

                    /** when draftId is unknown userId (targetUserId) is required */
                    if (
                        draftId == null &&
                        responseId == null &&
                        userId == null
                    ) {
                        return;
                    }

                    useDispatcher(
                        this,
                        reload({
                            draftId,
                            responseId,
                            targetUserId: userId,
                            templateVersionId,
                            userId: this._authService.user?.id,
                            timeZoneOffset: new Date().getTimezoneOffset(),
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        /** reset 'cause of the guard for evaluation/drafts?userId=<number>&templateVersionId=<number> routing to
         * evaluation/drafts/:draftId?userId=<number>&templateVersionId=<number> */
        useDispatcher(this, reset({}));
    }

    /** @private */
    _groups(): FormArray {
        return this._evalFormGroup?.controls?.groups as FormArray;
    }

    /** @private */
    _sections(group: AbstractControl | FormGroup): FormArray {
        return (group as FormGroup)?.controls?.sections as FormArray;
    }

    /** @private */
    _questions(section: AbstractControl | FormGroup): FormArray {
        return (section as FormGroup)?.controls?.questions as FormArray;
    }

    /** @private */
    _onSaveClick(): void {
        /* TODO cancel pending auto-save when this button is explicitly clicked */

        useDispatcher(
            this,
            save({
                formValue: this._evalFormGroup?.value,
                timeZoneOffset: new Date().getTimezoneOffset(),
            }),
        );
    }

    /** @private */
    _onPublishClick(): void {
        if (this.state.pending?.showPublishConfirmDialog === true) {
            return;
        }

        // TODO revise. how to inform users of required fields. also types???
        if (this._evalFormGroup.errors?.questionReasons != null) {
            console.log('required fields');
            return;
        }

        this._blockAutoSave = true;

        // TODO maybe unplug from reducer
        useDispatcher(this, showPublishConfirmDialog({}));

        let ref: CmpsDialogRef<CmpsConfirmDialogComponent, any>;
        let didClose = false; // prevent double hidePublishConfirmDialog action dispatch
        const sub = useSelector<boolean>(
            'evaluation.form.pending.showPublishConfirmDialog',
            () => null,
        )
            .pipe(
                tap((state) => {
                    if (!state) {
                        sub?.unsubscribe();
                        !didClose && ref?.close();
                        return;
                    }
                    const data: CmpsConfirmDialogOptions = {
                        title: this._translateService.instant(
                            TranslationKeyMap._9479_CONFIRMATION,
                        ),
                        message: this._translateService.instant(
                            TranslationKeyMap._10010_CONFIRM_YOU_WOULD_LIKE_TO_PUBLISH_THIS_EVALUATION,
                        ),
                        actions: [
                            {
                                label: this._translateService.instant(
                                    TranslationKeyMap._121_CANCEL,
                                ),
                                data: 1,
                            },
                            {
                                label: this._translateService.instant(
                                    TranslationKeyMap._9425_OK,
                                ),
                                data: 0,
                            },
                        ],
                    };
                    ref = this._dialog.open(CmpsConfirmDialogComponent, {
                        movable: true,
                        data,
                    });
                    ref.result
                        .pipe(
                            tap((ret: number) => {
                                didClose = true;
                                useDispatcher(
                                    this,
                                    hidePublishConfirmDialog({}),
                                );
                                if (ret === 0) {
                                    useDispatcher(
                                        this,
                                        save({
                                            formValue:
                                                this._evalFormGroup?.value,
                                            publish: true,
                                            timeZoneOffset:
                                                new Date().getTimezoneOffset(),
                                        }),
                                    );
                                } else {
                                    this._blockAutoSave = false;
                                }
                            }),
                            takeUntilDestroy(this),
                        )
                        .subscribe();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onUpdatePublishClick(): void {
        useDispatcher(
            this,
            save({
                formValue: this._evalFormGroup?.value,
                publish: true,
                timeZoneOffset: new Date().getTimezoneOffset(),
            }),
        );
    }

    /** @private */
    _onCloseEvalFormClick(): void {
        // TODO revise
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._router.navigate([
            this._activatedRoute.parent?.parent?.routeConfig?.path,
        ]);
    }

    /** @private */
    _onGroupToggleClick(index: number): void {
        const formArray = this._evalFormGroup?.get('groups') as FormArray;
        if (formArray == null || index < 0 || index >= formArray.length) {
            return;
        }

        const groupControl = formArray.at(index);

        const currentCollapsedValue = (groupControl.value as GroupFormValue)
            .collapsed;
        groupControl.patchValue(
            { collapsed: !currentCollapsedValue },
            { emitEvent: false },
        );
    }

    /** @private */
    _onExpandFormClick(collapsed: boolean): void {
        const formArray = this._evalFormGroup?.get('groups') as FormArray;
        const value = (formArray?.value as GroupFormValue[])?.map(() => ({
            collapsed,
        }));
        formArray.patchValue(value, { emitEvent: false });
    }

    /** @private */
    _onDropdownOpen(): void {
        adjustDropdownWidth(this._renderer, this._dropdownRef);
    }

    private _applyScoring(value: EvalFormValue): void {
        const nextValue = rollupScore(this._evalFormGroup, value);
        const ret = checkScore(this._evalFormGroup, nextValue);
        this._formErrors = {
            evalFail: ret.evalFail,
            groupFails: ret.groupFails,
            sectionFails: ret.sectionFails,
            questionFails: ret.questionFails,
        };

        this._evalFormGroup.patchValue(
            {
                score: ret.value.score,
                diffMaxScore: ret.value.diffMaxScore,
                percentage: ret.value.percentage,
                groups: ret.value.groups,
            },
            { emitEvent: false },
        );
    }

    private _sanitizeContactLength(): void {
        const contactLengthControl = this._evalFormGroup.get('contactLength');
        if (contactLengthControl?.value !== null) {
            const stringValue = contactLengthControl.value.toString();
            const sanitizedStringValue = stringValue.replace(/\D/g, '');
            const sanitizedNumber = parseInt(sanitizedStringValue, 10) || 0;

            if (sanitizedNumber !== contactLengthControl.value) {
                contactLengthControl.setValue(sanitizedNumber, {
                    emitEvent: false,
                });
            }
        }
    }

    private _loadQueues(): void {
        useSelector<QueueItem[]>('evaluation.form.queues', () => null)
            .pipe(
                filter((queues) => !!queues && queues.length > 0),
                tap((queues) => {
                    this._updateQueueOptions(queues);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _updateQueueOptions(queues: QueueItem[]): void {
        this._queueOpts = queues?.map((queue) => ({
            value: queue.id,
            label: queue.name,
        }));

        this._queueOpts.unshift({
            value: defaultQueueId,
            label: this._translateService.instant(TranslationKeyMap._842_N_A),
        });
    }
}
